/* eslint-disable office-addins/load-object-before-read */
/* global console window Office ResizeObserver cancelAnimationFrame requestAnimationFrame */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { updateCell } from "../../state/structureSlice";
import { measureAndDispatchOverflow } from "./cellAutofitUtils";
import { useCellStyleCalcs } from "./useCellStyleCalcs";
import { EditorContent, useEditor } from "@tiptap/react";
import { getSchema, getHTMLFromFragment, elementFromString } from "@tiptap/core";
import { Node as PMNode, DOMParser as PMDOMParser } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import "./editor.css";
import { select } from "../../state/selectionSlice";
import exists from "../../helpers/exists";
import { EventCategory, useGoogleAnalytics } from "@connectaryal/google-analytics";
import { updateStyle } from "../../state/cellStylesSlice";
import { ContentType } from "../../helpers/copyPaste";
import { BubbleMenu } from "@tiptap/react/menus";
import { Button } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Bold, Italic, Underline } from "@vectoricons/atlas-icons-react";
import CommonTooltip from "../common/CommonTooltip";
import { useMouseOverDocument } from "../../helpers/useMouseOverDocument";
import { debounce } from "../../helpers/debounce";

// Trailing-debounce window for autosaving cell edits without waiting for blur.
const AUTOSAVE_DEBOUNCE_MS = 600;

// Single source of truth for the extension set. Shared by the live editor
// (`useEditor`) and the headless display serializer (`generateHTML`) so a
// non-edited cell paints byte-equivalently to what the mounted editor would
// render for the same document (issue #5).
export const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    trailingNode: false,
  }),
  TextStyleKit,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
];

// Schema is derived from the extension set once and reused for every display-mode
// serialization. `generateHTML` would rebuild it per call — wasteful across the
// whole grid, the exact cost #5 is removing.
const DISPLAY_SCHEMA = getSchema(EDITOR_EXTENSIONS);

// Headless equivalent of `generateHTML(doc, EDITOR_EXTENSIONS)` reusing the cached
// schema. Produces byte-equivalent HTML to what the mounted editor renders.
function renderDisplayHTML(doc) {
  const node = PMNode.fromJSON(DISPLAY_SCHEMA, doc);
  return getHTMLFromFragment(node.content, DISPLAY_SCHEMA);
}

function getDPIRatio() {
  return exists("Office") ? (Office.context.platform === Office.PlatformType.Mac ? 0.75 : 1) : 1;
}

// Applies the cell's style to the editor's marks. When `prevStyle` is provided
// (a style-only update), only the marks whose source field actually changed are
// issued, and the whole select-all + transaction is skipped when nothing the
// editor cares about changed. Pass no `prevStyle` (or null) for a full apply —
// e.g. right after `setContent`, which clears all marks. This keeps applying a
// stylesheet to every cell from triggering a full per-cell mark rewrite.
function applyStyle(editor, currentStyle, prevStyle) {
  const full = prevStyle === undefined || prevStyle === null;
  // A preset switch flips the bold/italic/underline/textAlign branch wholesale,
  // so treat every field in that branch as changed when the preset changed.
  const presetChanged = full || currentStyle?.preset !== prevStyle?.preset;
  const changed = (k) => full || currentStyle?.[k] !== prevStyle?.[k];

  const isBody = currentStyle?.preset === "Body";
  const needsTextBranch =
    !isBody &&
    (presetChanged || changed("weight") || changed("style") || changed("fontUnderlined") || changed("textAlign"));
  const needsBodyTextAlign = isBody && presetChanged; // only unset alignment when switching into Body
  const needsFontFamily = changed("fontFamily");
  const needsSize = changed("size");
  const needsColor = changed("color");

  if (!needsTextBranch && !needsBodyTextAlign && !needsFontFamily && !needsSize && !needsColor) {
    return;
  }

  const cmds = editor.chain().selectAll();

  if (!isBody) {
    if (presetChanged || changed("weight")) {
      currentStyle?.weight === "bold" ? cmds.setBold() : cmds.unsetBold();
    }
    if (presetChanged || changed("style")) {
      currentStyle?.style === "italic" ? cmds.setItalic() : cmds.unsetItalic();
    }
    if (presetChanged || changed("fontUnderlined")) {
      currentStyle?.fontUnderlined ? cmds.setUnderline() : cmds.unsetUnderline();
    }
    if (presetChanged || changed("textAlign")) {
      currentStyle?.textAlign ? cmds.setTextAlign(currentStyle.textAlign) : cmds.setTextAlign("center");
    }
  } else if (needsBodyTextAlign) {
    cmds.unsetTextAlign();
  }

  if (needsFontFamily) {
    currentStyle?.fontFamily ? cmds.setFontFamily(currentStyle.fontFamily) : cmds.setFontFamily("Arial");
  }
  if (needsSize) {
    currentStyle?.size
      ? cmds.setFontSize(parseInt(currentStyle.size.replace("pt", "")) * getDPIRatio() + "pt")
      : cmds.unsetFontSize();
  }
  if (needsColor) {
    currentStyle?.color ? cmds.setColor(currentStyle.color) : cmds.unsetColor();
  }

  cmds.run();
}

function safeGetTransactionText(e) {
  if (e.transaction && e.transaction.steps) {
    if (e.transaction.steps.length > 0) {
      if (e.transaction.steps[0].slice?.content?.content) {
        if (e.transaction.steps[0].slice?.content?.content.length > 0) {
          return e.transaction.steps[0].slice?.content?.content[0]?.text;
        }
      }
    }
  }
  return "";
}

// Focus the freshly-mounted editor and place the caret. When a click coordinate
// was captured during the display → edit swap, map it to a document position via
// `posAtCoords`; otherwise (keyboard entry, or coords off the content) collapse to
// the end. Runs after `applyStyle`'s `selectAll`, so it also collapses that
// whole-document selection — without it the cell would appear fully selected on
// entry. Primes the caret-boundary ref so the first boundary arrow press can
// navigate to the next cell.
function placeCaretOnEntry(editor, pendingClickRef, previousCursorPosRef) {
  const coords = pendingClickRef?.current;
  let placed = false;
  if (coords) {
    try {
      const pos = editor.view.posAtCoords({ left: coords.left, top: coords.top });
      if (pos) {
        editor.chain().focus().setTextSelection(pos.pos).run();
        placed = true;
      }
    } catch (e) {
      // posAtCoords can throw before layout settles — fall back below.
    }
    pendingClickRef.current = null;
  }
  if (!placed) editor.chain().focus("end").run();
  // Entry at the end counts as "on last"; a click mid-content does not.
  previousCursorPosRef.current = { isCursorOnLast: !placed, isCursorOnFirst: false };
}

// Build a TipTap doc from legacy `text` (AI-generated / pre-json cells that store
// only `text`). The mounted editor initialises with `content: props.text`, which
// TipTap parses **as HTML** (so a stored `<b>foo</b>` renders bold, not as literal
// markup). We parse identically — same schema, same `preserveWhitespace: "full"` —
// so a never-edited cell lays out and renders exactly like the editor would.
function docFromText(text) {
  if (!text) return null;
  const dom = elementFromString(String(text));
  const node = PMDOMParser.fromSchema(DISPLAY_SCHEMA).parse(dom, { preserveWhitespace: "full" });
  return node.toJSON();
}

// Transform the stored doc into exactly what the mounted editor renders after
// `applyStyle` runs on entry, so display mode is pixel-identical to edit mode —
// including the line-box strut. (Putting font-size on the inner `textStyle` span,
// not the container, is what keeps the paragraph strut the same in both modes: the
// editor leaves the paragraph/container at the inherited default size and only the
// span carries the real size, so a smaller font does not shrink the line height.)
//
// Mirrors `applyStyle`:
//   • Every text node gets a `textStyle` mark with `currentStyle`'s font family
//     (default Arial), size (× DPI, when set) and color — these are always applied
//     uniformly, overriding any stale stored values.
//   • Non-Body presets force bold/italic/underline uniformly from `currentStyle`;
//     Body keeps the stored per-character marks (what lets bubble formatting persist).
//   • Paragraph `textAlign` is set to `currentStyle.textAlign` (or center) for
//     non-Body and unset for Body — matching `applyStyle`'s alignment handling.
function styleDocForDisplay(json, currentStyle) {
  if (!json) return json;
  const isBody = currentStyle?.preset === "Body";
  const textStyleMark = {
    type: "textStyle",
    attrs: {
      backgroundColor: null,
      color: currentStyle?.color ?? null,
      fontFamily: currentStyle?.fontFamily ?? "Arial",
      fontSize: currentStyle?.size ? parseInt(currentStyle.size.replace("pt", "")) * getDPIRatio() + "pt" : null,
      lineHeight: null,
    },
  };
  const uniformFormatting = [];
  if (!isBody) {
    if (currentStyle?.weight === "bold") uniformFormatting.push({ type: "bold" });
    if (currentStyle?.style === "italic") uniformFormatting.push({ type: "italic" });
    if (currentStyle?.fontUnderlined) uniformFormatting.push({ type: "underline" });
  }
  const paraTextAlign = isBody ? null : (currentStyle?.textAlign ?? "center");

  const walk = (node) => {
    const out = { ...node };
    if (out.type === "paragraph") {
      out.attrs = { ...(out.attrs ?? {}), textAlign: paraTextAlign };
    }
    if (out.type === "text") {
      const kept = isBody
        ? (out.marks ?? []).filter((m) => m.type === "bold" || m.type === "italic" || m.type === "underline")
        : [...uniformFormatting];
      out.marks = [textStyleMark, ...kept];
    }
    if (out.content) out.content = out.content.map(walk);
    return out;
  };
  return walk(json);
}

// =====================================================================================
// Display mode — inert, no ProseMirror. Every cell at rest renders through here so the
// app mounts at most one live editor (the actively-edited cell). Container styling and
// autofit measurement match edit mode exactly; content is produced by TipTap's headless
// serializer using EDITOR_EXTENSIONS for byte-parity with the mounted editor.
// =====================================================================================
const CellTextDisplay = (props) => {
  const dispatch = useDispatch();
  const currentStyle = useSelector((state) => state.gridd.present.cellStyles[props.id]);
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );

  // Resolve the doc once: stored json, else synthesize from legacy `text`.
  const displayJson = useMemo(() => props.json ?? docFromText(props.text), [props.json, props.text]);

  const { verticalAlign, pl, pr, pt, pb, width, height, lineHeight, paragraphSpacing, styleCalcsRef } =
    useCellStyleCalcs(displayJson, currentStyle, currentStyleSheet);

  const mouseOverDocument = useMouseOverDocument();
  const textContainerRef = useRef();
  const editorId = `editor_${props.x}_${props.y}`;

  // Headless render of the cell content, transformed to exactly what the editor
  // shows after `applyStyle` (font/size/color as inner span marks, alignment as
  // paragraph attrs) so display and edit are pixel-identical.
  const html = useMemo(() => {
    if (!displayJson) return "";
    try {
      return renderDisplayHTML(styleDocForDisplay(displayJson, currentStyle));
    } catch (e) {
      console.warn("Failed to render cell display content", e);
      return "";
    }
  }, [displayJson, currentStyle]);

  // Container mirrors the editor's `EditorContent` wrapper exactly: only color and
  // text-align (font/size/weight live on the inner span marks, as in edit mode, so
  // the paragraph line-box strut matches). `whiteSpace: pre-wrap` matches
  // ProseMirror's editable default so leading/repeated spaces (e.g. "   4") render
  // identically.
  const contentStyle = {
    color: currentStyle?.color,
    textAlign: currentStyle?.textAlign,
    whiteSpace: "pre-wrap",
  };

  // Autofit overflow measurement — identical to edit mode so the grid sizes the same
  // whether or not a cell is being edited. (See useCellStyleCalcs / cellAutofitUtils.)
  useEffect(() => {
    const el = textContainerRef.current;
    if (!el) return;
    const handle = requestAnimationFrame(() =>
      measureAndDispatchOverflow(el, styleCalcsRef.current, props.id, dispatch)
    );
    return () => cancelAnimationFrame(handle);
  }, [props.text, props.json, currentStyle]);

  useEffect(() => {
    const el = textContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let handle;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(handle);
      handle = requestAnimationFrame(() => measureAndDispatchOverflow(el, styleCalcsRef.current, props.id, dispatch));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(handle);
    };
  }, []);

  // Click intent is captured for caret placement; the actual selection (which puts
  // this cell first in the selection array and so swaps it into edit mode) is
  // dispatched by the parent Cell's onClick handler.
  const onMouseDown = (e) => {
    if (props.pendingClickRef) props.pendingClickRef.current = { left: e.clientX, top: e.clientY };
  };

  return (
    <div
      ref={textContainerRef}
      onMouseDown={onMouseDown}
      style={{
        position: "relative",
        width,
        height,
        left: pl,
        top: pt,
        bottom: pb,
        right: pr,
        alignContent: verticalAlign,
        lineHeight,
        overflow: "clip",
        overflowY: mouseOverDocument ? "auto" : "hidden",
      }}
    >
      <style>
        {`
          .tiptap {
            p {
              margin-block-end: 0;
              margin: 0pt 0pt ${paragraphSpacing}pt 0pt;
            }

            ul {
              display: inline-block;
              list-style-type: disc;
              margin: 0px 0px 0px 15px;
              padding: 0px 0px 0px 15px;
              padding-inline-start: 3px;
            }

            ol {
              font-size: ${parseInt(currentStyle?.size?.replace("pt", "") ?? "12") * getDPIRatio() + "pt"};
              display: inline-block;
              margin: 0px 0px 0px 15px;
              padding: 0px 0px 0px 15px;
            }
          }
        `}
      </style>
      <div id={editorId} className="tiptap" style={contentStyle} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

CellTextDisplay.propTypes = {
  id: PropTypes.string.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  text: PropTypes.string,
  json: PropTypes.object,
  pendingClickRef: PropTypes.object,
};

// =====================================================================================
// Edit mode — the full live TipTap editor. Mounted only for the actively-edited cell
// (the first cell in the selection array). Carries all of the #12 effects intact.
// =====================================================================================
const CellTextEditorActive = (props) => {
  const { t } = useTranslation();
  const { trackCustomEvent } = useGoogleAnalytics();
  const presentationHash = useSelector((state) => state.app.presentation.hash);
  const readOnly = useSelector((state) => state.app.readOnlyMode);
  const currentStyle = useSelector((state) => state.gridd.present.cellStyles[props.id]);
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const { verticalAlign, pl, pr, pt, pb, width, height, lineHeight, paragraphSpacing, styleCalcsRef } =
    useCellStyleCalcs(props.json, currentStyle, currentStyleSheet);

  const [lastSelection, setLastSelection] = useState({});
  // Shared across all cells via a single document-level listener (see hook).
  const mouseOverDocument = useMouseOverDocument();

  const textContainerRef = useRef();

  const dispatch = useDispatch();
  const editorId = `editor_${props.x}_${props.y}`;

  // Refs decouple the autosave from render-scope closures so a single stable
  // debounced function can read the latest values at flush time.
  const propsRef = useRef(props);
  propsRef.current = props;
  const trackCustomEventRef = useRef(trackCustomEvent);
  trackCustomEventRef.current = trackCustomEvent;
  const presentationHashRef = useRef(presentationHash);
  presentationHashRef.current = presentationHash;
  // Latest editor content, refreshed on every onUpdate. The autosave reads from
  // here (not the editor) so it can flush safely even after the editor is torn down.
  const pendingContentRef = useRef(null);
  // Baseline ("last committed") text used for the GA change-size event.
  const lastContentRef = useRef(props.text);
  // Stringified json of the most recent self-dispatched save, so the content
  // effect can ignore the round-trip echo and not reset the cursor mid-typing.
  const selfUpdateJsonRef = useRef(null);
  // Latest currentStyle, read by the content effect (which must re-apply style
  // after setContent clears marks) without subscribing that effect to style.
  const currentStyleRef = useRef(currentStyle);
  currentStyleRef.current = currentStyle;
  // The style currently applied to the editor's marks. Lets the style effect
  // issue only the marks that changed instead of an unconditional full rewrite.
  const appliedStyleRef = useRef(currentStyle);
  // Tracks whether the content effect has run once. The editor is created with
  // `content` already set, so the first run must NOT re-`setContent` (that would
  // clobber the entry caret); it only applies style and places the caret.
  const didInitContentRef = useRef(false);
  // Caret-boundary state for arrow-key cell navigation. A ref (not a render-scope
  // `let`) so it survives the re-renders that selection/typing trigger and is
  // shared between the entry effect and the keyup handler — otherwise each render
  // reset it and a single boundary arrow press never accumulated the two-keyup
  // state the navigation needs.
  const previousCursorPosRef = useRef({});

  // Commit the editor's current content to the store. Stable across renders
  // (reads mutable values from refs), so the debounced wrapper is also stable.
  const saveEditorContent = React.useCallback(() => {
    const pending = pendingContentRef.current;
    if (!pending) return;
    const { x, y } = propsRef.current;
    selfUpdateJsonRef.current = JSON.stringify(pending.json);
    dispatch(updateCell({ x, y, text: pending.text, html: pending.html, json: pending.json }));

    const lastContent = lastContentRef.current;
    if (lastContent && lastContent !== "") {
      trackCustomEventRef.current({
        name: "gridd_text_updated",
        category: EventCategory.CUSTOM,
        params: {
          textLengthChange: Math.abs(pending.text.length - lastContent.length),
          presId: presentationHashRef.current,
        },
      });
    }
    lastContentRef.current = pending.text;
  }, [dispatch]);

  const debouncedSave = React.useMemo(() => debounce(saveEditorContent, AUTOSAVE_DEBOUNCE_MS), [saveEditorContent]);
  const debouncedSaveRef = useRef(debouncedSave);
  debouncedSaveRef.current = debouncedSave;

  // Flush any pending autosave when the editor unmounts (selecting another cell,
  // switching slides, closing) so an unsaved edit is never lost.
  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const editor = useEditor(
    {
      extensions: EDITOR_EXTENSIONS,
      content: props.json ?? props.text,
      parseOptions: {
        preserveWhitespace: "full",
      },
      editorProps: {
        handlePaste: async (view, event, slice) => {
          console.log("on paste into TipTap editor", { editorId, view, event, slice });
          try {
            const parsed = await window.clipboardWithContext?.tryParseClipboard();
            if (parsed?.type === ContentType.Table) {
              console.log("Table detected in paste, using custom paste logic");
              editor.commands.setContent(parsed.content[0][0]);
              window.clipboardWithContext?.onParsed(parsed);
              return true;
            } else if (parsed?.type === ContentType.Gridd) {
              console.log("Gridd detected in paste, using custom paste logic");
              window.clipboardWithContext?.onParsed(parsed);
              return true;
            } else {
              const text = (parsed?.content ?? event.clipboardData?.getData("text/plain"))?.trim();
              view.dispatch(view.state.tr.deleteSelection());
              if (text) view.dispatch(view.state.tr.insertText(text));
              else view.dispatch(view.state.tr.insert(view.state.selection.$from.pos, slice.content));
              dispatch(
                updateCell({
                  x: props.x,
                  y: props.y,
                  text,
                  html: editor.getHTML(),
                  json: editor.getJSON(),
                })
              );
              return true;
            }
          } catch (error) {
            console.error("Error parsing clipboard", error);
          }

          return false;
        },
        handleKeyPress: (view, e) => {
          console.warn("TipTap key press event", e);
        },
      },
      onBlur: () => {
        // Commit immediately on blur rather than waiting out the debounce window.
        debouncedSaveRef.current.flush();
      },
      onSelectionUpdate: (e) => {
        //console.log("on editor selection update", { editorId, selection: e.transaction.selection, e });
        window._lastEditorUpdate = e;
        setLastSelection(e.transaction);
      },
      onUpdate: (e) => {
        const text = e.editor.getText({ blockSeparator: "\n" });
        // Track the latest content so the debounced autosave can commit it even
        // if the user never blurs the editor.
        pendingContentRef.current = { text, html: e.editor.getHTML(), json: e.editor.getJSON() };
        // console.log("on editor update", {
        //   editorId,
        //   props,
        //   text,
        //   e,
        //   isEmpty: e.editor.isEmpty,
        //   transactionText: safeGetTransactionText(e),
        // });
        if (
          // empty cell contents
          text === null ||
          text === "" ||
          text === safeGetTransactionText(e) // on first character enter
        ) {
          // then apply current or default preset style to the cell content
          let styleToApply = currentStyle;
          if (!currentStyle?.preset && currentStyleSheet) {
            const defaultPresetName = currentStyleSheet.defaults?.preset;
            const defaultPreset = currentStyleSheet.presets?.find((p) => p.name === defaultPresetName);
            if (defaultPreset) {
              const defaultStyle = {
                weight: currentStyle?.weight ?? defaultPreset.fontWeight,
                style: currentStyle?.style ?? defaultPreset.fontStyle,
                size: currentStyle?.size ?? defaultPreset.fontSize,
                color:
                  currentStyle?.color ??
                  currentStyleSheet.colors.find((c) => c.name === defaultPreset.fontColor)?.value ??
                  "black",
                fontFamily: currentStyle?.fontFamily ?? defaultPreset.fontFamily,
                textAlign: currentStyle?.textAlign ?? "center",
              };
              styleToApply = defaultStyle;
            }
          }
          applyStyle(e.editor, styleToApply);
          e.editor.commands.focus("end");
        }
      },
    },
    []
  );

  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [readOnly]);

  // Content sync + entry. Runs ONLY when the cell's text/json actually changes —
  // never on a style-only change. `setContent` is a full document reparse; running
  // it for every cell whenever a stylesheet/preset edit touches `currentStyle` was
  // the dominant LCP cost (issue #12). Style is re-applied here because `setContent`
  // clears all marks.
  useEffect(() => {
    if (!editor) return;

    const currentStyle = currentStyleRef.current;

    // First run = the editor just mounted (display → edit swap). `content` was
    // already parsed by useEditor, so do NOT re-`setContent` (that would clobber
    // the entry caret). Apply current style, then place the caret at the click
    // point / end. This is what makes a click land the caret instead of leaving
    // the whole cell selected.
    if (!didInitContentRef.current) {
      didInitContentRef.current = true;
      applyStyle(editor, currentStyle); // full apply over the freshly-parsed content
      appliedStyleRef.current = currentStyle;
      placeCaretOnEntry(editor, props.pendingClickRef, previousCursorPosRef);
      return;
    }

    // Ignore the store echo of our own autosave: a debounced save dispatches
    // updateCell and props.json flows straight back unchanged. Re-running
    // setContent/focus here would yank the cursor to the end mid-typing.
    if (selfUpdateJsonRef.current !== null && JSON.stringify(props.json) === selfUpdateJsonRef.current) {
      selfUpdateJsonRef.current = null;
      return;
    }

    editor.commands.setContent(props.json ?? props.text);
    applyStyle(editor, currentStyle); // full apply — setContent cleared marks
    appliedStyleRef.current = currentStyle;
    editor.commands.focus("end");
  }, [props.text, props.json, editor]);

  // Style sync. Runs when `currentStyle` changes (e.g. a preset/stylesheet apply)
  // without re-parsing content: applyStyle issues only the marks that changed and
  // skips entirely when no text-affecting field changed. The one-shot `bullets`
  // flag is processed here since it arrives as a style change.
  useEffect(() => {
    if (!editor) return;
    const prevApplied = appliedStyleRef.current;
    appliedStyleRef.current = currentStyle;
    applyStyle(editor, currentStyle, prevApplied);

    if (currentStyle?.bullets) {
      console.log("processing bullet style flag", currentStyle.bullets);
      editor.commands.selectAll();
      switch (currentStyle.bullets) {
        case "bullets":
          editor.commands.toggleBulletList();
          break;
        case "numbers":
          editor.commands.toggleList("orderedList", "listItem");
          break;
      }

      dispatch(
        updateCell({
          x: props.x,
          y: props.y,
          text: editor.getText(),
          html: editor.getHTML(),
          json: editor.getJSON(),
        })
      );

      dispatch(updateStyle({ id: props.id, bullets: null }));
    }
  }, [currentStyle, editor]);

  useEffect(() => {
    const el = textContainerRef.current;
    if (!el) return;
    const handle = requestAnimationFrame(() =>
      measureAndDispatchOverflow(el, styleCalcsRef.current, props.id, dispatch)
    );
    return () => cancelAnimationFrame(handle);
  }, [props.text, props.json, currentStyle]);

  useEffect(() => {
    const el = textContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let handle;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(handle);
      handle = requestAnimationFrame(() => measureAndDispatchOverflow(el, styleCalcsRef.current, props.id, dispatch));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(handle);
    };
  }, []);

  // Compute overflow values once after the first render to ensure initial values are correct.
  useEffect(() => {
    const el = textContainerRef.current;
    if (!el) return;
    const handle = requestAnimationFrame(() =>
      measureAndDispatchOverflow(el, styleCalcsRef.current, props.id, dispatch)
    );
    return () => cancelAnimationFrame(handle);
  }, []);

  // Sync missing json field from tiptap on (edit) mount for AI-generated slides.
  // When a slide is AI-generated, only `text` is stored — `json` is undefined.
  // Reading from the editor (which falls back to props.text on init) and persisting
  // it here upgrades the cell to json the first time it is edited. (Display mode
  // synthesizes json from text for measurement, so this no longer runs per cell on
  // startup — only for the cell the user actually edits.)
  useEffect(() => {
    if (!editor || props.json) return;
    dispatch(
      updateCell({
        x: props.x,
        y: props.y,
        text: props.text,
        html: editor.getHTML(),
        json: editor.getJSON(),
      })
    );
  }, [editor]);

  const [pressedKeys, setPressedKeys] = React.useState({});
  const showBubbleMenu = useSelector((state) => state.preferences.bubbleMenu);
  const uiScale = useSelector((state) => state.preferences.uiScale);

  return (
    <>
      <div
        ref={textContainerRef}
        style={{
          position: "relative",
          width,
          height,
          left: pl,
          top: pt,
          bottom: pb,
          right: pr,
          alignContent: verticalAlign,
          lineHeight,
          overflow: "clip",
          overflowY: mouseOverDocument ? "auto" : "hidden",
        }}
        onClick={(e) => {
          if (
            e.target.localName === "div" &&
            typeof e.target.className === "string" &&
            e.target.className.indexOf("tiptap") < 0 &&
            !editor.isFocused
          ) {
            console.warn("Clicked outside div target", e);
            editor.chain().focus("end", true).run();
            previousCursorPosRef.current = { isCursorOnLast: true };
          } else console.warn("Clicked inside div target", e);
        }}
        onKeyDown={(e) => {
          //console.log("TipTap div key down", e);

          const newPressedKeys = { ...pressedKeys };
          if (!e.metaKey && (pressedKeys["MetaLeft"] || pressedKeys["MetaRight"])) {
            Object.keys(newPressedKeys).forEach((key) => {
              newPressedKeys[key] = false;
            });
          }
          newPressedKeys[e.code] = true;
          setPressedKeys(newPressedKeys);
        }}
        onKeyUp={(e) => {
          console.log("TipTap div key up", { e, pressedKeys });

          if (lastSelection) {
            const isCursorOnLast =
              lastSelection.selection?.to === lastSelection.doc?.content?.size - 1 &&
              lastSelection.selection?.to === lastSelection.selection?.from;
            const nodeIsList =
              lastSelection.doc?.content?.content[0]?.type?.name === "bulletList" ||
              lastSelection.doc?.content?.content[0]?.type?.name === "orderedList";
            const isCursorOnFirst =
              lastSelection.selection?.to === (nodeIsList ? 3 : 1) &&
              lastSelection.selection?.to === lastSelection.selection?.from;

            // TODO: account for spacers and select them properly
            // Neighbour cells are selected by coordinate only (no stale id): the id
            // here would be THIS cell's, and carrying it onto the neighbour made two
            // cells match the "first-selected" edit check and mount two editors.
            if (e.code === "ArrowDown") {
              // test: if last character, arrow down should move to one cell down
              if (previousCursorPosRef.current.isCursorOnLast && isCursorOnLast) {
                dispatch(select({ cell: { x: props.x + 1, y: props.y }, type: "clear" }));
              }
            } else if (e.code === "ArrowRight") {
              if (previousCursorPosRef.current.isCursorOnLast && isCursorOnLast) {
                dispatch(select({ cell: { x: props.x, y: props.y + 1 }, type: "clear" }));
              }
            } else if (e.code === "ArrowUp") {
              if (previousCursorPosRef.current.isCursorOnFirst && isCursorOnFirst) {
                dispatch(select({ cell: { x: props.x - 1, y: props.y }, type: "clear" }));
              }
            } else if (e.code === "ArrowLeft") {
              if (previousCursorPosRef.current.isCursorOnFirst && isCursorOnFirst) {
                dispatch(select({ cell: { x: props.x, y: props.y - 1 }, type: "clear" }));
              }
            } else if (!e.ctrlKey && !pressedKeys["MetaLeft"] && !pressedKeys["MetaRight"]) {
              debouncedSaveRef.current(); // autosave shortly after the user stops typing, even without blur
            }

            previousCursorPosRef.current = { isCursorOnFirst, isCursorOnLast };
          }

          if (pressedKeys["MetaLeft"] || pressedKeys["MetaRight"] || e.ctrlKey) {
            if (pressedKeys["KeyC"]) {
              const selection = editor.state.selection;
              console.log("copy from cell text editor", { e, lastSelection, selection });

              if (selection.ranges.find((r) => r.$from.pos !== r.$to.pos)) {
                console.log("text range selected in cell, default copy logic done by Tiptap");
                return;
              }

              if (window.clipboardWithContext?.onCopy) {
                window.clipboardWithContext.onCopy(e, {
                  text: editor.getText({ blockSeparator: "\n" }),
                  json: editor.getJSON(),
                  x: props.x,
                  y: props.y,
                  id: props.id,
                });
              }
            } else if (pressedKeys["KeyA"]) {
              dispatch(select({ table: true }));
            } else if (pressedKeys["KeyB"]) {
              console.warn("Bolding???");
            }
          }

          const newPressedKeys = { ...pressedKeys };
          newPressedKeys[e.code] = false;
          if (e.key === "Meta") {
            Object.keys(newPressedKeys).forEach((key) => {
              newPressedKeys[key] = false;
            });
          }
          setPressedKeys(newPressedKeys);
        }}
      >
        {showBubbleMenu && (
          <BubbleMenu
            style={{
              transform: `scale(${uiScale ?? 1})`,
            }}
            editor={editor}
            options={{ placement: "top" }}
          >
            <div style={{ zIndex: 1300, display: "flex", flexDirection: "row", gap: 2 }}>
              <CommonTooltip content={t("Bold")}>
                <Button
                  size="small"
                  icon={<Bold size={12} />}
                  style={{
                    background: editor.isActive("bold") ? "#0065FD" : "white",
                    color: editor.isActive("bold") ? "white" : undefined,
                  }}
                  onClick={() => {
                    editor.chain().focus().toggleBold().run();
                    debouncedSaveRef.current();
                  }}
                />
              </CommonTooltip>
              <CommonTooltip content={t("Italic")}>
                <Button
                  size="small"
                  icon={<Italic size={12} />}
                  style={{
                    background: editor.isActive("italic") ? "#0065FD" : "white",
                    color: editor.isActive("italic") ? "white" : undefined,
                  }}
                  onClick={() => {
                    editor.chain().focus().toggleItalic().run();
                    debouncedSaveRef.current();
                  }}
                />
              </CommonTooltip>
              <CommonTooltip content={t("Underline")}>
                <Button
                  size="small"
                  icon={<Underline size={12} />}
                  style={{
                    background: editor.isActive("underline") ? "#0065FD" : "white",
                    color: editor.isActive("underline") ? "white" : undefined,
                  }}
                  onClick={() => {
                    editor.chain().focus().toggleUnderline().run();
                    debouncedSaveRef.current();
                  }}
                />
              </CommonTooltip>
            </div>
          </BubbleMenu>
        )}
        <style>
          {`
          .tiptap {
            p {
              margin-block-end: 0;
              margin: 0pt 0pt ${paragraphSpacing}pt 0pt;
            }

            ul {
              display: inline-block;
              list-style-type: disc;
              margin: 0px 0px 0px 15px;
              padding: 0px 0px 0px 15px;
              padding-inline-start: 3px;
            }

            ol {
              font-size: ${parseInt(currentStyle?.size?.replace("pt", "") ?? "12") * getDPIRatio() + "pt"};
              display: inline-block;
              margin: 0px 0px 0px 15px;
              padding: 0px 0px 0px 15px;
            }
          }
        `}
        </style>
        <EditorContent
          id={editorId}
          editor={editor}
          style={{ color: currentStyle?.color, textAlign: currentStyle?.textAlign }}
        />
      </div>
    </>
  );
};

CellTextEditorActive.propTypes = {
  id: PropTypes.string.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  text: PropTypes.string,
  json: PropTypes.object,
  pendingClickRef: PropTypes.object,
};

// =====================================================================================
// Dispatcher. A cell mounts the live editor only while it is the FIRST cell in the
// selection array (the actively-edited cell); otherwise it renders the inert display.
// Keeping the active editor on the first-selected cell means arrow-key cell-to-cell
// navigation (handled inside the editor) keeps working — moving the selection mounts
// the editor on the next cell, which auto-focuses.
// =====================================================================================
const CellTextEditor = (props) => {
  // The actively-edited cell is the FIRST cell in the selection array. Match by
  // coordinate (the cell's grid position): exactly one cell can satisfy this, so a
  // selection entry that carries a different cell's id can never mount two editors.
  const isEditing = useSelector((state) => {
    const first = state.selection.cells[0];
    return !!first && first.x === props.x && first.y === props.y;
  });

  // Persists across the display ↔ edit swap so a click in display mode can place
  // the caret once the editor mounts.
  const pendingClickRef = useRef(null);

  return isEditing ? (
    <CellTextEditorActive {...props} pendingClickRef={pendingClickRef} />
  ) : (
    <CellTextDisplay {...props} pendingClickRef={pendingClickRef} />
  );
};

CellTextEditor.propTypes = {
  id: PropTypes.string.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  text: PropTypes.string,
  json: PropTypes.object,
};

export default CellTextEditor;
